import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, CardMedia, Typography, Button, Container, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Styled components
const ViewDetailsButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#5acef5',
  color: 'white',
  '&:hover': {
    backgroundColor: '#3db1dc',
  },
  textTransform: 'none',
  borderRadius: '4px',
  padding: '8px 16px',
}));

const BlogTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  height: '3.6em',
}));

const BlogExcerpt = styled(Typography)(({ theme }) => ({
  color: 'rgba(0, 0, 0, 0.6)',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  height: '4.5em',
}));

const DateBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  backgroundColor: '#1a365d',
  color: 'white',
  padding: '8px 16px',
  borderTopLeftRadius: '4px',
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(2, 0),
}));

const CarouselTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  transition: 'transform 0.5s ease-in-out',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
}));

const BlogCard = ({ post, onViewDetails }) => {
  return (
    <Card sx={{ 
      width: '100%',
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      borderRadius: '8px',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={post.imageUrl}
          alt={post.title}
        />
        <DateBadge>
          <Typography variant="body2">{post.date}</Typography>
        </DateBadge>
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <BlogTitle variant="h6" component="div">
          {post.title}
        </BlogTitle>
        <BlogExcerpt variant="body2">
          {post.excerpt}
        </BlogExcerpt>
        <Box sx={{ mt: 'auto' }}>
          <ViewDetailsButton onClick={() => onViewDetails(post.id)}>
            View Details
          </ViewDetailsButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const BlogCardCarousel = () => {
  // Example data structure - replace with your actual data
  const blogPosts = [
    {
      id: 1,
      title: 'HOW SWP AND STP TAXATION WORKS...',
      excerpt: 'Systematic Withdrawal Plan (SWP) and Systematic Transfer Plan (STP) are mutual f...',
      imageUrl: 'https://source.unsplash.com/random/800x600?finance',
      date: '28-Mar-23',
    },
    {
      id: 2,
      title: 'LEVERAGING THE PSU RALLY TO SUSTAIN CAPE...',
      excerpt: 'Indian equities are one of the brighter corners of sunshine in the global market...',
      imageUrl: 'https://source.unsplash.com/random/800x600?trading',
      date: '28-Jun-24',
    },
    {
      id: 3,
      title: 'CONTINUED BENEFITS OF DEBT FUNDS AND ITS...',
      excerpt: 'On 24th March 2023, the Finance Minister proposed an amendment to the Finance Bi...',
      imageUrl: 'https://source.unsplash.com/random/800x600?investment',
      date: '10-Apr-23',
    },
    {
      id: 4,
      title: 'MARKET TRENDS 2025: WHAT TO EXPECT',
      excerpt: 'As we approach the new fiscal year, investors should be aware of emerging trends that will shape the market landscape...',
      imageUrl: 'https://source.unsplash.com/random/800x600?stocks',
      date: '15-Feb-25',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const [isPaused, setIsPaused] = useState(false);

  // Handle responsive visible cards
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setVisibleCards(1);
      } else if (window.innerWidth < 960) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto slide functionality
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        handleNext();
      }, 5000); // Change slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [currentIndex, isPaused, visibleCards]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === blogPosts.length - visibleCards ? 0 : prevIndex + 1
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? blogPosts.length - visibleCards : prevIndex - 1
    );
  };

  const handleViewDetails = (id) => {
    console.log(`View details for post ${id}`);
    // Add your navigation or modal opening logic here
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h2" sx={{ 
          fontWeight: 'bold',
          display: 'inline-block',
          '& .highlight': {
            color: '#5acef5'
          }
        }}>
          LATEST <span className="highlight">BLOG</span>
        </Typography>
        <Box sx={{ 
          width: '60px', 
          height: '4px', 
          backgroundColor: '#1a365d', 
          margin: '16px auto' 
        }} />
      </Box>

      <CarouselContainer
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <NavigationButton 
          onClick={handlePrev} 
          sx={{ left: { xs: '0', md: '-20px' } }}
        >
          <ArrowBackIosNewIcon />
        </NavigationButton>
        
        <NavigationButton 
          onClick={handleNext} 
          sx={{ right: { xs: '0', md: '-20px' } }}
        >
          <ArrowForwardIosIcon />
        </NavigationButton>
        
        <CarouselTrack
          sx={{
            transform: `translateX(-${(currentIndex * (100 / visibleCards))}%)`,
            '& > div': {
              flex: `0 0 ${100 / visibleCards}%`,
              px: 2
            }
          }}
        >
          {blogPosts.map((post) => (
            <Box key={post.id}>
              <BlogCard post={post} onViewDetails={handleViewDetails} />
            </Box>
          ))}
        </CarouselTrack>
      </CarouselContainer>
      
      {/* Pagination dots */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {Array.from({ length: blogPosts.length - visibleCards + 1 }).map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentIndex(index)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: currentIndex === index ? '#5acef5' : '#ccc',
              margin: '0 4px',
              cursor: 'pointer'
            }}
          />
        ))}
      </Box>
    </Container>
  );
};

export default BlogCardCarousel;